import { useId, useState, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { cx } from './cx';
import { Icon } from './basics';
import type { IconName } from './icons';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  label: string;
  hint?: string;
  error?: string | undefined;
  prefix?: ReactNode;
}

/** A labelled input, 17px so Android doesn't zoom; its hint or error is announced with it. */
export function TextField({ label, hint, error, prefix, id, className, ...rest }: TextFieldProps) {
  const made = useId();
  const inputId = id ?? made;
  const described = hint || error ? `${inputId}-d` : undefined;
  return (
    <div className={cx('zb-field', error && 'zb-field--error', className)}>
      <label htmlFor={inputId} className="zb-field__label">
        {label}
      </label>
      <div className="zb-field__box">
        {prefix && <span className="zb-field__prefix">{prefix}</span>}
        <input id={inputId} className="zb-field__input" aria-invalid={error ? true : undefined} aria-describedby={described} {...rest} />
      </div>
      {(error || hint) && (
        <div id={described} className="zb-field__hint">
          {error && <Icon name="triangle-alert" size={16} />}
          {error || hint}
        </div>
      )}
    </div>
  );
}

export interface SegmentedOption {
  value: string;
  label: string;
  icon?: IconName;
}

export interface SegmentedControlProps {
  options: SegmentedOption[];
  value?: string;
  onChange?: (value: string) => void;
  label: string;
  className?: string;
}

/** A radio group drawn as one pill; the selected option is inverse, never colour alone. */
export function SegmentedControl({ options, value, onChange, label, className }: SegmentedControlProps) {
  const [own, setOwn] = useState(value ?? options[0]?.value);
  const current = value ?? own;
  return (
    <div role="radiogroup" aria-label={label} className={cx('zb-seg', className)}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={current === o.value}
          className={cx('zb-seg__opt', current === o.value && 'is-on')}
          onClick={() => {
            setOwn(o.value);
            onChange?.(o.value);
          }}
        >
          {o.icon && <Icon name={o.icon} size={20} />}
          {o.label}
        </button>
      ))}
    </div>
  );
}
export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: string;
  error?: string | undefined;
}

/** The capture box: type it the way you would on WhatsApp. */
export function Textarea({ label, hint, error, id, className, ...rest }: TextareaProps) {
  const made = useId();
  const fieldId = id ?? made;
  const described = hint || error ? `${fieldId}-d` : undefined;
  return (
    <div className={cx('zb-field', 'zb-textarea', error && 'zb-field--error', className)}>
      <label htmlFor={fieldId} className="zb-field__label">
        {label}
      </label>
      <div className="zb-field__box">
        <textarea id={fieldId} className="zb-field__input" aria-invalid={error ? true : undefined} aria-describedby={described} {...rest} />
      </div>
      {(error || hint) && (
        <div id={described} className="zb-field__hint">
          {error && <Icon name="triangle-alert" size={16} />}
          {error || hint}
        </div>
      )}
    </div>
  );
}

export interface CodeInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
  hint?: string;
  error?: string | undefined;
}

/** Six big digits; the number pad, and the code WhatsApp delivered offered by the keyboard. "123 456" is fine. */
export function CodeInput({ label, hint, error, id, className, ...rest }: CodeInputProps) {
  const made = useId();
  const fieldId = id ?? made;
  const described = hint || error ? `${fieldId}-d` : undefined;
  return (
    <div className={cx('zb-field', 'zb-code', error && 'zb-field--error', className)}>
      <label htmlFor={fieldId} className="zb-field__label">
        {label}
      </label>
      <div className="zb-field__box">
        <input
          id={fieldId}
          className="zb-field__input"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={8}
          aria-invalid={error ? true : undefined}
          aria-describedby={described}
          {...rest}
        />
      </div>
      {(error || hint) && (
        <div id={described} className="zb-field__hint">
          {error && <Icon name="triangle-alert" size={16} />}
          {error || hint}
        </div>
      )}
    </div>
  );
}
